import { Typography, Stack, Box } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { useMemo } from 'react'
import { addSeconds } from 'date-fns/esm'
import { preview } from '@/components/form/preview'
import { dateUtils } from '@/utils'
import { FormatTime } from '@/components/smartUtils/time/FormatTime'
import { Tooltip } from '@/components/primitives/Tooltip'

import { useTexts } from '../../useTexts'
import {
  fromPodBarcodeToPodBarcodeOrUnscanned,
  getBarcodesStatusText,
  mergeBarcodesWithDriverBarcodes,
  type MergedBarcode,
} from './utils'

interface Props {
  extOrder: uui.domain.client.rm.ExtendedOrderStep
}

export function Barcodes(props: Props) {
  const { extOrder } = props
  const texts = useTexts()

  const barcodes = extOrder.orderStep.trackingData?.pods?.barcodes || []
  const driverBarcodes = extOrder.orderStep.trackingData?.pods?.driverBarcodes || []

  const mergedBarcodesByStatus = useMemo(() => {
    const regularBarcodes = fromPodBarcodeToPodBarcodeOrUnscanned(barcodes, extOrder.orderStep)
    return mergeBarcodesWithDriverBarcodes(regularBarcodes, driverBarcodes)
  }, [barcodes, driverBarcodes, extOrder.orderStep])

  const entries = Object.entries(mergedBarcodesByStatus) as [
    uui.domain.client.rm.BarcodePod['barcodeStatus'],
    MergedBarcode[],
  ][]

  return (
    <Stack spacing={2} data-testid="execution-barcodes">
      {entries.map(([barcodeStatus, podByStatus]) => {
        return (
          <preview.Field
            key={barcodeStatus}
            label={`${getBarcodesStatusText(barcodeStatus, texts)} (${podByStatus.length})`}
            testId={`execution-barcodes-${barcodeStatus.toLowerCase()}`}
          >
            {podByStatus.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                -
              </Typography>
            ) : (
              <>
                {podByStatus.map((pod, index) => {
                  const podDate =
                    pod.sec > 0 ? addSeconds(dateUtils.getDateAtMidnight(), pod.sec) : null
                  return (
                    <Box
                      key={`${pod.barcode}_${index}`}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: index < podByStatus.length - 1 ? '8px' : 0,
                      }}
                    >
                      <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                        {pod.barcode}
                      </Typography>
                      {pod.isDriverBarcode && podDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mr: '12px' }}>
                          <Typography
                            sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}
                          >
                            <FormatTime source={podDate} />
                          </Typography>
                          <Tooltip title={texts.addedFromDriver.label} placement="bottom">
                            <InfoOutlined
                              sx={{
                                fontSize: 20,
                                display: 'flex',
                                color: t => t.vars.palette.action.active,
                                pb: '2px',
                              }}
                            />
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </>
            )}
          </preview.Field>
        )
      })}
    </Stack>
  )
}
